<div class="insurance-container" *ngIf="!loading; else loadingTemplate">
  <!-- Back button -->
  <button mat-icon-button (click)="onCancel()" class="back-button">
    <mat-icon>arrow_back</mat-icon>
  </button>

  <!-- Insurance Management Form -->
  <mat-card class="insurance-form-card">
    <mat-card-header>
      <mat-card-title>
        <h2>Insurance Information</h2>
      </mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <form [formGroup]="insuranceForm" (ngSubmit)="onSave()">

        <!-- Primary Insurance Section -->
        <div class="insurance-section">
          <div class="insurance-section-header">
            <mat-icon>health_and_safety</mat-icon>
            Primary Insurance
          </div>

          <div class="form-row">
            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Insurance Provider</mat-label>
              <mat-select formControlName="primaryInsuranceProvider">
                <mat-option *ngFor="let provider of insuranceProviders" [value]="provider">
                  {{ provider }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Member ID</mat-label>
              <input matInput formControlName="primarySubscriberId" placeholder="123456789">
            </mat-form-field>
          </div>

          <div class="form-row">
            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Group Number</mat-label>
              <input matInput formControlName="primaryGroupNumber" placeholder="Optional">
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Policy Holder Name</mat-label>
              <input matInput formControlName="primaryPolicyHolderName" placeholder="If different from patient">
              <mat-hint>Leave blank if you are the policy holder</mat-hint>
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="form-field full-width" *ngIf="insuranceForm.get('primaryPolicyHolderName')?.value">
            <mat-label>Policy Holder Date of Birth</mat-label>
            <input matInput formControlName="primaryPolicyHolderDOB" placeholder="MM/DD/YYYY">
          </mat-form-field>

          <!-- Primary Insurance Card Upload -->
          <div class="upload-cards-row">
            <div class="file-upload-section compact">
              <label class="file-upload-label">Insurance Card (Front)</label>
              <div class="file-upload-container">
                <input
                  type="file"
                  accept="image/*"
                  name="primaryInsuranceCardFront"
                  (change)="onFileSelected($event, 'primaryInsuranceCardFront')"
                  #primaryCardFrontInput
                  style="display: none;"
                  [disabled]="isUploading('primaryInsuranceCardFront')">

                <button
                  mat-stroked-button
                  color="primary"
                  type="button"
                  class="upload-btn"
                  (click)="primaryCardFrontInput.click()"
                  *ngIf="!hasFile('primaryInsuranceCardFront')">
                  <mat-icon>add_photo_alternate</mat-icon>
                  Add Front
                </button>

                <div *ngIf="hasFile('primaryInsuranceCardFront')" class="file-uploaded">
                  <mat-icon color="primary">photo</mat-icon>
                  <span>Front Selected</span>
                  <button mat-icon-button color="warn" type="button" (click)="removeFile('primaryInsuranceCardFront')">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="file-upload-section compact">
              <label class="file-upload-label">Insurance Card (Back)</label>
              <div class="file-upload-container">
                <input
                  type="file"
                  accept="image/*"
                  name="primaryInsuranceCardBack"
                  (change)="onFileSelected($event, 'primaryInsuranceCardBack')"
                  #primaryCardBackInput
                  style="display: none;"
                  [disabled]="isUploading('primaryInsuranceCardBack')">

                <button
                  mat-stroked-button
                  color="primary"
                  type="button"
                  class="upload-btn"
                  (click)="primaryCardBackInput.click()"
                  *ngIf="!hasFile('primaryInsuranceCardBack')"
                  [disabled]="isUploading('primaryInsuranceCardBack')">
                  <mat-icon *ngIf="!isUploading('primaryInsuranceCardBack')">add_photo_alternate</mat-icon>
                  <mat-spinner *ngIf="isUploading('primaryInsuranceCardBack')" [diameter]="20"></mat-spinner>
                  {{ isUploading('primaryInsuranceCardBack') ? 'Selecting...' : 'Add Back' }}
                </button>

                <div *ngIf="hasFile('primaryInsuranceCardBack')" class="file-uploaded">
                  <mat-icon color="primary">check_circle</mat-icon>
                  <span>Back Selected</span>
                  <button mat-icon-button color="warn" type="button" (click)="removeFile('primaryInsuranceCardBack')">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add Secondary Insurance Button -->
        <div class="add-secondary-section" *ngIf="!showSecondaryInsurance">
          <button
            mat-stroked-button
            type="button"
            class="add-secondary-btn"
            (click)="toggleSecondaryInsurance()">
            <mat-icon>add_circle_outline</mat-icon>
            Add Secondary Insurance
          </button>
        </div>

        <!-- Secondary Insurance Section -->
        <div class="insurance-section" *ngIf="showSecondaryInsurance">
          <div class="insurance-section-header">
            <mat-icon>health_and_safety</mat-icon>
            Secondary Insurance
            <button
              mat-icon-button
              type="button"
              class="remove-btn"
              (click)="toggleSecondaryInsurance()">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="form-row">
            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Insurance Provider</mat-label>
              <mat-select formControlName="secondaryInsuranceProvider">
                <mat-option *ngFor="let provider of insuranceProviders" [value]="provider">
                  {{ provider }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Member ID</mat-label>
              <input matInput formControlName="secondarySubscriberId" placeholder="123456789">
            </mat-form-field>
          </div>

          <div class="form-row">
            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Group Number</mat-label>
              <input matInput formControlName="secondaryGroupNumber" placeholder="Optional">
            </mat-form-field>

            <mat-form-field appearance="outline" class="form-field">
              <mat-label>Policy Holder Name</mat-label>
              <input matInput formControlName="secondaryPolicyHolderName" placeholder="If different from patient">
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="form-field full-width" *ngIf="insuranceForm.get('secondaryPolicyHolderName')?.value">
            <mat-label>Policy Holder Date of Birth</mat-label>
            <input matInput formControlName="secondaryPolicyHolderDOB" placeholder="MM/DD/YYYY">
          </mat-form-field>

          <!-- Secondary Insurance Card Upload -->
          <div class="upload-cards-row">
            <div class="file-upload-section compact">
              <label class="file-upload-label">Insurance Card (Front)</label>
              <div class="file-upload-container">
                <input
                  type="file"
                  accept="image/*"
                  name="secondaryInsuranceCardFront"
                  (change)="onFileSelected($event, 'secondaryInsuranceCardFront')"
                  #secondaryCardFrontInput
                  style="display: none;"
                  [disabled]="isUploading('secondaryInsuranceCardFront')">

                <button
                  mat-stroked-button
                  color="primary"
                  type="button"
                  class="upload-btn"
                  (click)="secondaryCardFrontInput.click()"
                  *ngIf="!hasFile('secondaryInsuranceCardFront')"
                  [disabled]="isUploading('secondaryInsuranceCardFront')">
                  <mat-icon *ngIf="!isUploading('secondaryInsuranceCardFront')">add_photo_alternate</mat-icon>
                  <mat-spinner *ngIf="isUploading('secondaryInsuranceCardFront')" [diameter]="20"></mat-spinner>
                  {{ isUploading('secondaryInsuranceCardFront') ? 'Selecting...' : 'Add Front' }}
                </button>

                <div *ngIf="hasFile('secondaryInsuranceCardFront')" class="file-uploaded">
                  <mat-icon color="primary">check_circle</mat-icon>
                  <span>Front Selected</span>
                  <button mat-icon-button color="warn" type="button" (click)="removeFile('secondaryInsuranceCardFront')">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="file-upload-section compact">
              <label class="file-upload-label">Insurance Card (Back)</label>
              <div class="file-upload-container">
                <input
                  type="file"
                  accept="image/*"
                  name="secondaryInsuranceCardBack"
                  (change)="onFileSelected($event, 'secondaryInsuranceCardBack')"
                  #secondaryCardBackInput
                  style="display: none;"
                  [disabled]="isUploading('secondaryInsuranceCardBack')">

                <button
                  mat-stroked-button
                  color="primary"
                  type="button"
                  class="upload-btn"
                  (click)="secondaryCardBackInput.click()"
                  *ngIf="!hasFile('secondaryInsuranceCardBack')"
                  [disabled]="isUploading('secondaryInsuranceCardBack')">
                  <mat-icon *ngIf="!isUploading('secondaryInsuranceCardBack')">add_photo_alternate</mat-icon>
                  <mat-spinner *ngIf="isUploading('secondaryInsuranceCardBack')" [diameter]="20"></mat-spinner>
                  {{ isUploading('secondaryInsuranceCardBack') ? 'Selecting...' : 'Add Back' }}
                </button>

                <div *ngIf="hasFile('secondaryInsuranceCardBack')" class="file-uploaded">
                  <mat-icon color="primary">check_circle</mat-icon>
                  <span>Back Selected</span>
                  <button mat-icon-button color="warn" type="button" (click)="removeFile('secondaryInsuranceCardBack')">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="button-container">
          <button mat-raised-button color="primary" type="submit" [disabled]="!isFormEdited">
            <mat-icon>save</mat-icon>
            Save Insurance Information
          </button>
          <button mat-button type="button" (click)="onCancel()">Cancel</button>
        </div>
      </form>
    </mat-card-content>
  </mat-card>
</div>

<ng-template #loadingTemplate>
  <div class="loading-container">
    <mat-spinner [diameter]="50" color="primary"></mat-spinner>
  </div>
</ng-template>